import { useEffect, useLayoutEffect, useState, useSyncExternalStore, } from 'react';
/**
 * Barre d'azione fissate in fondo allo schermo (il `FormFooter` dei pannelli)
 * attualmente montate: il `Toaster` le legge per alzare i toast sopra il
 * footer invece di coprirne i bottoni («Chiudi», «Salva»).
 */
let mountedBottomBars = 0;
const listeners = new Set();
function notify() {
    listeners.forEach((listener) => listener());
}
function subscribe(listener) {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}
const hasBottomBar = () => mountedBottomBars > 0;
const hasBottomBarOnServer = () => false;
/**
 * Dichiara una barra in fondo finché il componente è montato E `active`
 * (default `true`): una barra che non è davvero in fondo allo schermo (es. il
 * footer di un form in un dialogo centrato) non deve alzare i toast.
 */
export function useRegisterBottomBar(active = true) {
    useEffect(() => {
        if (!active)
            return;
        mountedBottomBars += 1;
        notify();
        return () => {
            mountedBottomBars -= 1;
            notify();
        };
    }, [active]);
}
/** Scarto ammesso fra il fondo della barra e il fondo della viewport. */
export const VIEWPORT_BOTTOM_TOLERANCE_PX = 2;
/** La barra tocca il fondo della viewport (ed è visibile). */
export function isPinnedToViewportBottom(rect, viewportHeight) {
    return (rect.height > 0 &&
        Math.abs(viewportHeight - rect.bottom) <= VIEWPORT_BOTTOM_TOLERANCE_PX);
}
/**
 * `true` mentre l'elemento sta davvero sul fondo della viewport (il footer di
 * un pannello laterale), `false` se è in mezzo allo schermo (un dialogo
 * centrato) o nascosto. Si rimisura al ridimensionamento della finestra o
 * dell'elemento e a fine animazione (apertura di pannelli e dialoghi).
 */
export function useIsPinnedToViewportBottom(ref) {
    const [pinned, setPinned] = useState(false);
    useLayoutEffect(() => {
        const element = ref.current;
        if (!element || typeof window === 'undefined')
            return;
        const measure = () => setPinned(isPinnedToViewportBottom(element.getBoundingClientRect(), window.innerHeight));
        measure();
        window.addEventListener('resize', measure);
        document.addEventListener('animationend', measure, true);
        document.addEventListener('transitionend', measure, true);
        const observer = typeof ResizeObserver === 'undefined'
            ? null
            : new ResizeObserver(measure);
        observer?.observe(element);
        return () => {
            window.removeEventListener('resize', measure);
            document.removeEventListener('animationend', measure, true);
            document.removeEventListener('transitionend', measure, true);
            observer?.disconnect();
        };
    }, [ref]);
    return pinned;
}
/** `true` mentre almeno una barra in fondo è montata. */
export function useHasBottomBar() {
    return useSyncExternalStore(subscribe, hasBottomBar, hasBottomBarOnServer);
}
//# sourceMappingURL=bottom-bar-presence.js.map